import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, 
  CheckCircle2, 
  Circle,
  Calendar,
  User,
  ClipboardList,
  ExternalLink,
  Image as ImageIcon,
  FileDown,
  Plus,
  AlertTriangle,
  Upload,
  Pencil,
  Trash2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const AuditDetailPage = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [headerSettings, setHeaderSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingTasks, setCreatingTasks] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [newItem, setNewItem] = useState({
    photo_url: null,
    photo_preview: null,
    location: '',
    finding: '',
    rectification: '',
    rectification_date: '',
    responsible_person_ids: []
  });
  const [addingItem, setAddingItem] = useState(false);
  const fileInputRef = useRef(null);
  
  // Edit item dialog state
  const [showEditItemDialog, setShowEditItemDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const editFileInputRef = useRef(null);
  
  // Delete confirmation state
  const [deletingItemId, setDeletingItemId] = useState(null);
  
  // Control completion dialog state
  const [showControlDialog, setShowControlDialog] = useState(false);
  const [controlItemId, setControlItemId] = useState(null);
  const [controlPhoto, setControlPhoto] = useState({ url: null, preview: null });
  const [controlComment, setControlComment] = useState('');
  const [submittingControl, setSubmittingControl] = useState(false);
  const controlFileInputRef = useRef(null);
  
  // PDF Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportStatus, setExportStatus] = useState(''); // 'preparing', 'processing', 'downloading', 'done', 'error'
  
  const canEditAudits = true; // Všichni uživatelé mají přístup

  useEffect(() => {
    fetchData();
  }, [auditId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [auditRes, headerRes, usersRes] = await Promise.all([
        api.get(`/audits/${auditId}`),
        api.get('/audit-header-settings'),
        api.get('/users/list')
      ]);
      setAudit(auditRes.data);
      setHeaderSettings(headerRes.data);
      setUsers(usersRes.data.filter(u => u.approved));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Chyba při načítání dat');
      navigate('/audity');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleControl = async (itemId) => {
    // Find the item to check if we're completing or uncompleting
    const item = audit.items?.find(i => i.id === itemId);
    if (!item) return;
    
    if (item.control_status === 'pending') {
      // Opening dialog for photo/comment when completing
      setControlItemId(itemId);
      setControlPhoto({ url: null, preview: null });
      setControlComment('');
      setShowControlDialog(true);
    } else {
      // Uncompleting - no photo needed
      try {
        const response = await api.put(`/audits/${auditId}/items/${itemId}/control`, {});
        setAudit(response.data);
        toast.success('Kontrola zrušena');
      } catch (error) {
        console.error('Error toggling control:', error);
        toast.error('Chyba při aktualizaci stavu');
      }
    }
  };

  // Upload image to server storage (Tigris) and return URL
  const uploadImageToServer = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/upload/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    
    return response.data.url; // Returns URL like /api/storage/images/xxx.jpg
  };

  // Get full image URL - handles both base64 (legacy) and server URLs
  const getImageUrl = (url) => {
    if (!url) return null;
    // If it's a base64 data URL, return as-is
    if (url.startsWith('data:')) return url;
    // If it's an absolute URL, return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    // If it's a relative API path, prepend the backend URL
    if (url.startsWith('/api/')) {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      return `${backendUrl}${url}`;
    }
    // Otherwise return as-is
    return url;
  };

  // Create preview from file for immediate display
  const createPreview = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  };

  const handleControlPhotoUpload = async (file) => {
    try {
      // Show preview immediately
      const preview = await createPreview(file);
      setControlPhoto({ url: null, preview: preview });
      
      // Upload to server
      const serverUrl = await uploadImageToServer(file);
      setControlPhoto({ url: serverUrl, preview: preview });
      toast.success('Fotografie nahrána');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Chyba při nahrávání fotografie');
      setControlPhoto({ url: null, preview: null });
    }
  };

  const handleSubmitControl = async () => {
    if (!controlPhoto.url && !controlComment.trim()) {
      toast.error('Nahrajte fotografii nebo zadejte komentář');
      return;
    }
    
    try {
      setSubmittingControl(true);
      const response = await api.put(`/audits/${auditId}/items/${controlItemId}/control`, {
        photo_url: controlPhoto.url || null,
        comment: controlComment.trim() || null
      });
      setAudit(response.data);
      setShowControlDialog(false);
      setControlItemId(null);
      setControlPhoto({ url: null, preview: null });
      setControlComment('');
      toast.success('Kontrola potvrzena');
    } catch (error) {
      console.error('Error confirming control:', error);
      toast.error(error.response?.data?.detail || 'Chyba při potvrzení kontroly');
    } finally {
      setSubmittingControl(false);
    }
  };

  const handleCreateTasks = async () => {
    if (!window.confirm('Vytvořit úkoly pro všechny nálezy této inspekce?')) return;
    
    try {
      setCreatingTasks(true);
      const response = await api.post(`/audits/${auditId}/create-tasks`);
      toast.success(`Vytvořeno ${response.data.task_count} úkolů`);
      await fetchData();
    } catch (error) {
      console.error('Error creating tasks:', error);
      toast.error(error.response?.data?.detail || 'Chyba při vytváření úkolů');
    } finally {
      setCreatingTasks(false);
    }
  };

  const handleCreateMissingTasks = async () => {
    if (!window.confirm('Vytvořit úkoly pro nálezy bez přiřazeného úkolu?')) return;
    
    try {
      setCreatingTasks(true);
      const response = await api.post(`/audits/${auditId}/create-missing-tasks`);
      toast.success(`Vytvořeno ${response.data.task_count} nových úkolů`);
      await fetchData();
    } catch (error) {
      console.error('Error creating missing tasks:', error);
      toast.error(error.response?.data?.detail || 'Chyba při vytváření úkolů');
    } finally {
      setCreatingTasks(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    try {
      // Show preview immediately
      const preview = await createPreview(file);
      setNewItem(prev => ({ ...prev, photo_url: null, photo_preview: preview }));
      
      // Upload to server
      const serverUrl = await uploadImageToServer(file);
      setNewItem(prev => ({ ...prev, photo_url: serverUrl, photo_preview: preview }));
      toast.success('Fotografie nahrána');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Chyba při nahrávání fotografie');
      setNewItem(prev => ({ ...prev, photo_url: null, photo_preview: null }));
    }
  };

  const handleAddItem = async () => {
    if (!newItem.location.trim()) {
      toast.error('Zadejte místo');
      return;
    }
    if (!newItem.rectification.trim()) {
      toast.error('Zadejte nápravu');
      return;
    }
    if (!newItem.rectification_date) {
      toast.error('Zadejte datum nápravy');
      return;
    }
    if (!newItem.responsible_person_ids || newItem.responsible_person_ids.length === 0) {
      toast.error('Vyberte alespoň jednu zodpovědnou osobu');
      return;
    }

    try {
      setAddingItem(true);
      await api.post(`/audits/${auditId}/items`, {
        photo_url: newItem.photo_url,
        location: newItem.location,
        finding: newItem.finding,
        rectification: newItem.rectification,
        rectification_date: newItem.rectification_date,
        responsible_person_ids: newItem.responsible_person_ids
      });
      toast.success('Nález přidán');
      setShowAddItemDialog(false);
      setNewItem({
        photo_url: null,
        photo_preview: null,
        location: '',
        finding: '',
        rectification: '',
        rectification_date: '',
        responsible_person_ids: []
      });
      await fetchData();
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error('Chyba při přidávání nálezu');
    } finally {
      setAddingItem(false);
    }
  };

  const handleEditItem = (item) => {
    // Support both new and legacy format
    const personIds = item.responsible_person_ids || (item.responsible_person_id ? [item.responsible_person_id] : []);
    setEditingItem({
      ...item,
      responsible_person_ids: personIds,
      photo_preview: item.photo_url
    });
    setShowEditItemDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem.location.trim()) {
      toast.error('Zadejte místo');
      return;
    }
    if (!editingItem.rectification.trim()) {
      toast.error('Zadejte nápravu');
      return;
    }
    if (!editingItem.rectification_date) {
      toast.error('Zadejte datum nápravy');
      return;
    }
    if (!editingItem.responsible_person_ids || editingItem.responsible_person_ids.length === 0) {
      toast.error('Vyberte alespoň jednu zodpovědnou osobu');
      return;
    }

    try {
      setSavingEdit(true);
      await api.put(`/audits/${auditId}/items/${editingItem.id}`, {
        photo_url: editingItem.photo_url,
        location: editingItem.location,
        finding: editingItem.finding,
        rectification: editingItem.rectification,
        rectification_date: editingItem.rectification_date,
        responsible_person_ids: editingItem.responsible_person_ids
      });
      toast.success('Nález upraven');
      setShowEditItemDialog(false);
      setEditingItem(null);
      await fetchData();
    } catch (error) {
      console.error('Error updating item:', error);
      toast.error('Chyba při úpravě nálezu');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Opravdu chcete smazat tento nález?')) return;
    
    try {
      setDeletingItemId(itemId);
      await api.delete(`/audits/${auditId}/items/${itemId}`);
      toast.success('Nález smazán');
      await fetchData();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Chyba při mazání nálezu');
    } finally {
      setDeletingItemId(null);
    }
  };

  const handleEditPhotoUpload = async (file) => {
    try {
      // Show preview immediately
      const preview = await createPreview(file);
      setEditingItem(prev => ({ ...prev, photo_url: null, photo_preview: preview }));
      
      // Upload to server
      const serverUrl = await uploadImageToServer(file);
      setEditingItem(prev => ({ ...prev, photo_url: serverUrl, photo_preview: preview }));
      toast.success('Fotografie nahrána');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Chyba při nahrávání fotografie');
      setEditingItem(prev => ({ ...prev, photo_url: null, photo_preview: null }));
    }
  };

  const handleExportPdf = async () => {
    const itemCount = audit?.items?.length || 0;
    
    // Show progress dialog for large audits
    setShowExportDialog(true);
    setExportProgress(0);
    setExportStatus('preparing');
    
    try {
      // Simulate progress for preparing
      setExportStatus('preparing');
      setExportProgress(10);
      
      // Start the export
      setExportStatus('processing');
      setExportProgress(30);
      
      const response = await api.get(`/audits/${auditId}/pdf`, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setExportProgress(30 + Math.round(percentCompleted * 0.6)); // 30-90%
          }
        }
      });
      
      setExportStatus('downloading');
      setExportProgress(90);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `inspekce_${audit.audit_number}_${audit.audit_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setExportStatus('done');
      setExportProgress(100);
      
      // Close dialog after short delay
      setTimeout(() => {
        setShowExportDialog(false);
        toast.success('PDF exportováno');
      }, 1000);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setExportStatus('error');
      setTimeout(() => {
        setShowExportDialog(false);
        toast.error('Chyba při exportu PDF. Zkuste to prosím znovu.');
      }, 2000);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    return dateStr;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!audit) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-slate-500">Inspekce nenalezena</p>
        </div>
      </Layout>
    );
  }

  const completedCount = audit.items?.filter(i => i.control_status === 'completed').length || 0;
  const totalCount = audit.items?.length || 0;

  return (
    <Layout>
      <div className="p-6 max-w-6xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/audity')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na seznam
        </Button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          {/* Document Header */}
          {headerSettings && (
            <div className="border-b pb-4 mb-4">
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div>
                  <h1 className="text-xl font-bold text-slate-800">
                    {headerSettings.document_number} {headerSettings.document_title}
                  </h1>
                  <p className="text-sm text-slate-500 mt-1">
                    Verze: {headerSettings.version} | Revize: {headerSettings.revision} | 
                    Účinný od: {headerSettings.effective_from}
                  </p>
                </div>
                <div className="text-right text-sm text-slate-600">
                  <p>Datum vydání: {headerSettings.issue_date}</p>
                  <p>Datum revize: {headerSettings.revision_date}</p>
                </div>
              </div>
            </div>
          )}

          {/* Audit Info */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                Inspekce č. {audit.audit_number}
              </h2>
              <div className="flex items-center gap-4 mt-2 text-slate-500">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(audit.audit_date)}
                </span>
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {audit.created_by_name}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* PDF Export button */}
              <Button 
                variant="outline"
                onClick={handleExportPdf}
                data-testid="export-pdf-button"
              >
                <FileDown className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              
              {/* Add Finding button */}
              {canEditAudits && (
                <Button 
                  onClick={() => setShowAddItemDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="add-finding-button"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Přidat nález
                </Button>
              )}
              
              {/* Create all tasks button */}
              {!audit.tasks_created && canEditAudits && totalCount > 0 && (
                <Button 
                  onClick={handleCreateTasks}
                  disabled={creatingTasks}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="create-tasks-button"
                >
                  <ClipboardList className="w-4 h-4 mr-2" />
                  {creatingTasks ? 'Vytváření...' : 'Vytvořit úkoly'}
                </Button>
              )}
              
              {/* Create missing tasks button - shows when some items don't have tasks */}
              {audit.tasks_created && canEditAudits && audit.items?.some(i => !i.linked_task_id) && (
                <Button 
                  onClick={handleCreateMissingTasks}
                  disabled={creatingTasks}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="create-missing-tasks-button"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {creatingTasks ? 'Vytváření...' : 'Vytvořit chybějící úkoly'}
                </Button>
              )}
              
              {audit.tasks_created && audit.event_id && (
                <Button 
                  variant="outline"
                  onClick={() => navigate(`/ukoly/${audit.event_id}`)}
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Zobrazit úkoly
                </Button>
              )}
            </div>
          </div>

          {/* Progress */}
          {totalCount > 0 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-slate-600 mb-1">
                <span>Zkontrolováno</span>
                <span>{completedCount}/{totalCount}</span>
              </div>
              <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    completedCount === totalCount ? 'bg-green-500' : 'bg-emerald-500'
                  }`}
                  style={{ width: `${(completedCount / totalCount) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Č.</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Foto</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Místo</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Náprava</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Termín</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Zodpovědná osoba</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Kontrola</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Foto splnění</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Komentář splnění</th>
                  {canEditAudits && (
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Akce</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y">
                {audit.items?.map((item, index) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-4 text-sm text-slate-600 font-medium">
                      {item.number || index + 1}
                    </td>
                    <td className="px-4 py-4">
                      {item.photo_url ? (
                        <img 
                          src={getImageUrl(item.photo_url)} 
                          alt={`Nález ${item.number}`}
                          className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80"
                          onClick={() => setSelectedImage(getImageUrl(item.photo_url))}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-slate-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-slate-800">{item.location}</div>
                      {item.finding && (
                        <div className="text-xs text-slate-500 mt-1">{item.finding}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600 max-w-xs">
                      {item.rectification}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600 whitespace-nowrap">
                      {formatDate(item.rectification_date)}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600">
                      {/* Support both new and legacy format */}
                      {item.responsible_person_names?.length > 0 
                        ? item.responsible_person_names.join(', ')
                        : item.responsible_person_name || '-'}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <button
                        onClick={() => handleToggleControl(item.id)}
                        className={`p-2 rounded-full transition-colors ${
                          item.control_status === 'completed'
                            ? 'text-green-600 hover:bg-green-100'
                            : 'text-slate-400 hover:bg-slate-100'
                        }`}
                        data-testid={`control-toggle-${item.id}`}
                      >
                        {item.control_status === 'completed' ? (
                          <CheckCircle2 className="w-6 h-6" />
                        ) : (
                          <Circle className="w-6 h-6" />
                        )}
                      </button>
                      {item.control_status === 'completed' && item.control_completed_by_name && (
                        <div className="text-xs text-slate-500 mt-1">
                          {item.control_completed_by_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {item.control_completed_photo ? (
                        <img 
                          src={getImageUrl(item.control_completed_photo)} 
                          alt={`Splnění ${item.number}`}
                          className="w-16 h-16 object-cover rounded cursor-pointer hover:opacity-80 mx-auto"
                          onClick={() => setSelectedImage(getImageUrl(item.control_completed_photo))}
                        />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center mx-auto">
                          <ImageIcon className="w-6 h-6 text-slate-300" />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-600 max-w-xs">
                      {item.control_completed_comment || '-'}
                    </td>
                    {canEditAudits && (
                      <td className="px-4 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEditItem(item)}
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors"
                            title="Upravit nález"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteItem(item.id)}
                            disabled={deletingItemId === item.id}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                            title="Smazat nález"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(!audit.items || audit.items.length === 0) && (
            <div className="p-12 text-center text-slate-500">
              Tato inspekce nemá žádné nálezy
            </div>
          )}
        </div>

        {/* Image Preview Dialog */}
        <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Fotografie nálezu</DialogTitle>
            </DialogHeader>
            {selectedImage && (
              <img 
                src={selectedImage} 
                alt="Nález" 
                className="w-full rounded"
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Add Finding Dialog */}
        <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Přidat nový nález</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              {/* Photo Upload */}
              <div>
                <Label>Fotografie</Label>
                <div className="mt-2">
                  {newItem.photo_preview ? (
                    <div className="relative">
                      <img 
                        src={newItem.photo_preview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setNewItem(prev => ({ ...prev, photo_url: null, photo_preview: null }))}
                      >
                        Odstranit
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
                    >
                      <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Klikněte pro nahrání fotografie</p>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePhotoUpload(file);
                    }}
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <Label htmlFor="location">Místo *</Label>
                <Input
                  id="location"
                  value={newItem.location}
                  onChange={(e) => setNewItem(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="např. Kuchyně, Sklad, Výroba..."
                  data-testid="add-item-location"
                />
              </div>

              {/* Finding */}
              <div>
                <Label htmlFor="finding">Nález / Popis</Label>
                <Textarea
                  id="finding"
                  value={newItem.finding}
                  onChange={(e) => setNewItem(prev => ({ ...prev, finding: e.target.value }))}
                  placeholder="Popis zjištěného nedostatku..."
                  rows={2}
                  data-testid="add-item-finding"
                />
              </div>

              {/* Rectification */}
              <div>
                <Label htmlFor="rectification">Náprava *</Label>
                <Textarea
                  id="rectification"
                  value={newItem.rectification}
                  onChange={(e) => setNewItem(prev => ({ ...prev, rectification: e.target.value }))}
                  placeholder="Popis nápravného opatření..."
                  rows={2}
                  data-testid="add-item-rectification"
                />
              </div>

              {/* Rectification Date */}
              <div>
                <Label htmlFor="rectification_date">Termín nápravy *</Label>
                <Input
                  id="rectification_date"
                  type="date"
                  value={newItem.rectification_date}
                  onChange={(e) => setNewItem(prev => ({ ...prev, rectification_date: e.target.value }))}
                  data-testid="add-item-date"
                />
              </div>

              {/* Responsible Person */}
              <div>
                <Label>Zodpovědné osoby * <span className="text-xs text-slate-500 font-normal">(vyberte jednu nebo více)</span></Label>
                <div className="mt-2 border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={newItem.responsible_person_ids?.includes(u.id) || false}
                        onChange={(e) => {
                          const ids = newItem.responsible_person_ids || [];
                          if (e.target.checked) {
                            setNewItem(prev => ({ ...prev, responsible_person_ids: [...ids, u.id] }));
                          } else {
                            setNewItem(prev => ({ ...prev, responsible_person_ids: ids.filter(id => id !== u.id) }));
                          }
                        }}
                        className="w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                        data-testid={`add-item-responsible-${u.id}`}
                      />
                      <span className="text-sm">{u.name}</span>
                    </label>
                  ))}
                </div>
                {newItem.responsible_person_ids?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Vybráno: {newItem.responsible_person_ids.length} {newItem.responsible_person_ids.length === 1 ? 'osoba' : newItem.responsible_person_ids.length < 5 ? 'osoby' : 'osob'}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
              <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
                Zrušit
              </Button>
              <Button 
                onClick={handleAddItem}
                disabled={addingItem}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="confirm-add-item"
              >
                {addingItem ? 'Ukládám...' : 'Přidat nález'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Finding Dialog */}
        <Dialog open={showEditItemDialog} onOpenChange={setShowEditItemDialog}>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Upravit nález č. {editingItem?.number}</DialogTitle>
            </DialogHeader>
            {editingItem && (
              <div className="space-y-4 overflow-y-auto flex-1 pr-2">
                {/* Photo Upload */}
                <div>
                  <Label>Fotografie</Label>
                  <div 
                    className="mt-1 border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors border-slate-300 hover:border-blue-400"
                    onClick={() => editFileInputRef.current?.click()}
                  >
                    <input 
                      ref={editFileInputRef}
                      type="file" 
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleEditPhotoUpload(file);
                      }}
                      className="hidden"
                    />
                    {editingItem.photo_preview ? (
                      <div>
                        <img 
                          src={editingItem.photo_preview} 
                          alt="Nález" 
                          className="max-h-32 mx-auto rounded"
                        />
                        <p className="text-xs text-slate-500 mt-2">Klikněte pro změnu</p>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">Klikněte pro nahrání</p>
                      </div>
                    )}
                  </div>
                  {editingItem.photo_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-red-600 hover:text-red-700"
                      onClick={() => setEditingItem(prev => ({ ...prev, photo_url: null, photo_preview: null }))}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Odstranit fotku
                    </Button>
                  )}
                </div>

                {/* Location */}
                <div>
                  <Label>Místo *</Label>
                  <Input
                    value={editingItem.location}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="např. Suchý sklad"
                  />
                </div>

                {/* Finding */}
                <div>
                  <Label>Nález / Popis</Label>
                  <Textarea
                    value={editingItem.finding || ''}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, finding: e.target.value }))}
                    placeholder="Popis zjištěného nedostatku..."
                    rows={2}
                  />
                </div>

                {/* Rectification */}
                <div>
                  <Label>Náprava *</Label>
                  <Textarea
                    value={editingItem.rectification}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, rectification: e.target.value }))}
                    placeholder="Popis nápravného opatření..."
                    rows={2}
                  />
                </div>

                {/* Rectification Date */}
                <div>
                  <Label>Termín nápravy *</Label>
                  <Input
                    type="date"
                    value={editingItem.rectification_date}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, rectification_date: e.target.value }))}
                  />
                </div>

                {/* Responsible Person */}
                <div>
                  <Label>Zodpovědné osoby * <span className="text-xs text-slate-500 font-normal">(vyberte jednu nebo více)</span></Label>
                  <div className="mt-2 border rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
                    {users.map((u) => (
                      <label key={u.id} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={editingItem.responsible_person_ids?.includes(u.id) || false}
                          onChange={(e) => {
                            const ids = editingItem.responsible_person_ids || [];
                            if (e.target.checked) {
                              setEditingItem(prev => ({ ...prev, responsible_person_ids: [...ids, u.id] }));
                            } else {
                              setEditingItem(prev => ({ ...prev, responsible_person_ids: ids.filter(id => id !== u.id) }));
                            }
                          }}
                          className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                        />
                        <span className="text-sm">{u.name}</span>
                      </label>
                    ))}
                  </div>
                  {editingItem.responsible_person_ids?.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      Vybráno: {editingItem.responsible_person_ids.length} {editingItem.responsible_person_ids.length === 1 ? 'osoba' : editingItem.responsible_person_ids.length < 5 ? 'osoby' : 'osob'}
                    </p>
                  )}
                </div>
              </div>
            )}
            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
              <Button variant="outline" onClick={() => {
                setShowEditItemDialog(false);
                setEditingItem(null);
              }}>
                Zrušit
              </Button>
              <Button 
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {savingEdit ? 'Ukládám...' : 'Uložit změny'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Control Completion Dialog */}
        <Dialog open={showControlDialog} onOpenChange={setShowControlDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Potvrzení splnění</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Pro potvrzení splnění je nutné nahrát fotografii <strong>nebo</strong> zadat komentář.
              </p>
              
              <div>
                <Label>Fotografie splnění</Label>
                <div className="mt-2">
                  {controlPhoto.preview ? (
                    <div className="relative">
                      <img 
                        src={controlPhoto.preview} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded"
                      />
                      <Button
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => setControlPhoto({ url: null, preview: null })}
                      >
                        Odstranit
                      </Button>
                    </div>
                  ) : (
                    <div
                      onClick={() => controlFileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-400 transition-colors"
                    >
                      <Upload className="w-6 h-6 mx-auto text-slate-400 mb-2" />
                      <p className="text-sm text-slate-500">Klikněte pro nahrání fotografie</p>
                    </div>
                  )}
                  <input
                    ref={controlFileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleControlPhotoUpload(file);
                    }}
                  />
                </div>
              </div>

              <div className="text-center text-sm text-slate-400">nebo</div>

              <div>
                <Label htmlFor="control-comment">Komentář</Label>
                <Textarea
                  id="control-comment"
                  value={controlComment}
                  onChange={(e) => setControlComment(e.target.value)}
                  placeholder="Popis splnění / poznámka..."
                  rows={3}
                  className="mt-2"
                  data-testid="control-comment"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowControlDialog(false);
                setControlItemId(null);
                setControlPhoto({ url: null, preview: null });
                setControlComment('');
              }}>
                Zrušit
              </Button>
              <Button 
                onClick={handleSubmitControl}
                disabled={submittingControl || (!controlPhoto.url && !controlComment.trim())}
                className="bg-green-600 hover:bg-green-700"
                data-testid="confirm-control"
              >
                {submittingControl ? 'Ukládám...' : 'Potvrdit splnění'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PDF Export Progress Dialog */}
        <Dialog open={showExportDialog} onOpenChange={() => {}}>
          <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileDown className="w-5 h-5" />
                Export PDF
              </DialogTitle>
            </DialogHeader>
            <div className="py-6">
              <div className="space-y-4">
                {/* Status message */}
                <div className="text-center">
                  {exportStatus === 'preparing' && (
                    <p className="text-slate-600">Připravuji export...</p>
                  )}
                  {exportStatus === 'processing' && (
                    <p className="text-slate-600">
                      Zpracovávám {audit?.items?.length || 0} nálezů s fotografiemi...
                    </p>
                  )}
                  {exportStatus === 'downloading' && (
                    <p className="text-slate-600">Stahuji soubor...</p>
                  )}
                  {exportStatus === 'done' && (
                    <p className="text-emerald-600 font-medium">Export dokončen!</p>
                  )}
                  {exportStatus === 'error' && (
                    <p className="text-red-600 font-medium">Chyba při exportu</p>
                  )}
                </div>
                
                {/* Progress bar */}
                <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className={`h-3 rounded-full transition-all duration-300 ${
                      exportStatus === 'error' ? 'bg-red-500' : 
                      exportStatus === 'done' ? 'bg-emerald-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
                
                {/* Progress percentage */}
                <p className="text-center text-sm text-slate-500">
                  {exportProgress}%
                </p>
                
                {/* Spinner for processing */}
                {(exportStatus === 'preparing' || exportStatus === 'processing') && (
                  <div className="flex justify-center">
                    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                )}
                
                {/* Info for large exports */}
                {exportStatus === 'processing' && audit?.items?.length > 20 && (
                  <p className="text-xs text-slate-400 text-center">
                    Export velkého množství nálezů může trvat déle. Prosím vyčkejte...
                  </p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Floating add button for mobile */}
        {canEditAudits && (
          <button
            onClick={() => setShowAddItemDialog(true)}
            className="fixed bottom-6 right-6 md:hidden w-14 h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-transform active:scale-95"
            title="Přidat nález"
          >
            <Plus className="w-7 h-7" />
          </button>
        )}
      </div>
    </Layout>
  );
};

export default AuditDetailPage;
